import {
  ApiCampaignPopupDefault,
  ApiCampaignPopupRule,
  CampaignPopupDefault,
  CampaignPopupRule,
  formatCampaignDisplay,
  parseCampaignInput,
  TextDirection,
} from "./types";

function asTextDirection(value: unknown): TextDirection {
  return value === "ltr" ? "ltr" : "rtl";
}

function resolveCampaignField(raw: Record<string, unknown>): string | string[] {
  const value =
    raw.campaign ??
    raw.Campaign ??
    raw.utmCampaign ??
    raw.utm_campaign ??
    "";
  return value as string | string[];
}

/** Accept legacy camelCase keys from older saved configs */
export function normalizeApiRule(raw: Record<string, unknown>): ApiCampaignPopupRule {
  const campaign = resolveCampaignField(raw);
  return {
    campaign: campaign as string | string[],
    title: String(raw.title ?? ""),
    body: String(raw.body ?? ""),
    ctaText: String(raw.ctaText ?? raw.ctaButtonLabel ?? ""),
    dismissText: String(raw.dismissText ?? raw.dismissLabel ?? ""),
    dir: asTextDirection(raw.dir ?? raw.textDirection),
    utm_source:
      raw.utm_source != null
        ? String(raw.utm_source)
        : raw.utmSource != null
          ? String(raw.utmSource)
          : undefined,
    utm_medium:
      raw.utm_medium != null
        ? String(raw.utm_medium)
        : raw.utmMedium != null
          ? String(raw.utmMedium)
          : undefined,
    enabled: raw.enabled !== false,
    showOncePerSession: raw.showOncePerSession !== false,
    updatedAt:
      typeof raw.updatedAt === "string" ? raw.updatedAt : undefined,
  };
}

export function apiRuleToForm(rule: ApiCampaignPopupRule): CampaignPopupRule {
  return {
    enabled: rule.enabled !== false,
    campaign: formatCampaignDisplay(rule.campaign),
    utm_source: rule.utm_source || "",
    utm_medium: rule.utm_medium || "",
    title: rule.title || "",
    body: rule.body || "",
    ctaText: rule.ctaText || "",
    dismissText: rule.dismissText || "",
    dir: rule.dir || "rtl",
    showOncePerSession: rule.showOncePerSession !== false,
    updatedAt: rule.updatedAt,
  };
}

export function normalizeApiDefaultPopup(
  raw: Record<string, unknown>
): ApiCampaignPopupDefault {
  return {
    enabled: raw.enabled !== false,
    title: String(raw.title ?? ""),
    body: String(raw.body ?? ""),
    ctaText: String(raw.ctaText ?? raw.ctaButtonLabel ?? ""),
    dismissText: String(raw.dismissText ?? raw.dismissLabel ?? ""),
    dir: asTextDirection(raw.dir ?? raw.textDirection),
    showOncePerSession: raw.showOncePerSession !== false,
  };
}

export function apiDefaultToForm(
  popup: ApiCampaignPopupDefault
): CampaignPopupDefault {
  return {
    enabled: popup.enabled !== false,
    title: popup.title || "",
    body: popup.body || "",
    ctaText: popup.ctaText || "",
    dismissText: popup.dismissText ?? "",
    dir: popup.dir || "rtl",
    showOncePerSession: popup.showOncePerSession !== false,
  };
}

export function formDefaultToApi(
  popup: CampaignPopupDefault
): ApiCampaignPopupDefault {
  const api: ApiCampaignPopupDefault = {
    enabled: popup.enabled,
    title: popup.title.trim(),
    body: popup.body.trim(),
    ctaText: popup.ctaText.trim(),
    dir: popup.dir,
    showOncePerSession: popup.showOncePerSession,
  };
  const dismiss = popup.dismissText?.trim();
  if (dismiss) {
    api.dismissText = dismiss;
  }
  return api;
}

export function formRuleToApi(rule: CampaignPopupRule): ApiCampaignPopupRule {
  const campaign = parseCampaignInput(rule.campaign);
  const apiRule: ApiCampaignPopupRule = {
    campaign,
    title: rule.title.trim(),
    body: rule.body.trim(),
    ctaText: rule.ctaText.trim(),
    dismissText: rule.dismissText.trim(),
    dir: rule.dir,
    enabled: rule.enabled,
    showOncePerSession: rule.showOncePerSession,
  };
  const src = rule.utm_source?.trim();
  const med = rule.utm_medium?.trim();
  if (src) apiRule.utm_source = src;
  if (med) apiRule.utm_medium = med;
  return apiRule;
}
